import * as React from 'react';
import clsx from 'clsx';
import BackgroundBubbles from '@/components/common/BackgroundBubbles';
import waveElementChat from '@/assets/images/svg/wave_element_chat.svg';
import { MockChatInput } from '@/components/chat/input/MockChatInput';
import { MockChatHeader } from '@/components/chat/mock/MockChatHeader';
import { MockEchoBubble } from '@/components/chat/mock/MockEchoBubble';
import { MockUserBubble } from '@/components/chat/mock/MockUserBubble';
import { MockEchoAudioBubble } from '@/components/chat/mock/MockEchoAudioBubble';
import { MockUserAudioBubble } from '@/components/chat/mock/MockUserAudioBubble';
import { SuggestedQuestions } from '@/features/my-echo/components/suggested-questions/SuggestedQuestions';
import { getUtmEchoConfig } from '@/components/layout/auth/utmEchoConfig';
import type { UtmEchoChatMessage } from '@/components/layout/auth/utmEchoConfig';

import marcusAvatar from '@/assets/images/echo/marcus_echo.webp';

/** Default (non-UTM) config for Marcus Aurelius. */
const MARCUS_MESSAGES: UtmEchoChatMessage[] = [
  {
    sender: 'echo',
    type: 'text',
    text: 'You have power over your mind - not outside events. Realize this, and you will find strength.',
  },
  {
    sender: 'user',
    type: 'text',
    text: 'That is easier said than done when the world feels chaotic.',
  },
  { sender: 'echo', type: 'audio' },
  { sender: 'user', type: 'audio' },
];

interface AuthChatPreviewCardProps {
  hasUtm: boolean;
  utmEcho: string | null;
}

export const AuthChatPreviewCard: React.FC<AuthChatPreviewCardProps> = ({ hasUtm, utmEcho }) => {
  const config = hasUtm ? getUtmEchoConfig(utmEcho) : null;
  const avatar = config?.avatar ?? marcusAvatar;
  const headerName = config?.headerName ?? 'Marcus Aurelius';
  const headerCategory = config?.headerCategory ?? 'Philosophy • Stoicism';
  const messages = config?.messages ?? MARCUS_MESSAGES;
  const suggestedQuestion = config?.suggestedQuestion ?? 'How do I apply Stoicism today?';
  const compact = true;

  return (
    <div className='relative flex size-full flex-col overflow-hidden rounded-[35px] bg-chat-bg-gradient'>
      {/* Ambient Layers */}
      <div className='pointer-events-none absolute inset-0 z-0 select-none overflow-hidden'>
        <BackgroundBubbles count={30} />
        <div
          className={clsx(
            'absolute bottom-0 left-1/2 h-auto w-full',
            'opacity-80',
            '-translate-x-1/2 translate-y-[60%]'
          )}
        >
          <img src={waveElementChat} alt='' className='size-full animate-spin-slow-120' />
        </div>
      </div>

      {/* Header */}
      <div className='relative z-20 shrink-0'>
        <MockChatHeader name={headerName} category={headerCategory} avatarSrc={avatar} />
      </div>

      {/* Messages */}
      <div className='relative z-10 flex flex-1 flex-col justify-end overflow-hidden px-4 pb-2'>
        <div className={clsx('flex flex-col justify-end', compact ? 'gap-2' : 'gap-3')}>
          {messages.map((msg, i) => {
            const key = `${msg.sender}-${msg.type}-${i}`;
            if (msg.sender === 'echo' && msg.type === 'text' && msg.text) {
              return (
                <MockEchoBubble key={key} message={msg.text} compact={compact} className='!mb-0' />
              );
            }
            if (msg.sender === 'echo' && msg.type === 'audio') {
              return <MockEchoAudioBubble key={key} avatarSrc={avatar} compact={compact} />;
            }
            if (msg.sender === 'user' && msg.type === 'text' && msg.text) {
              return (
                <MockUserBubble key={key} message={msg.text} compact={compact} className='!mb-0' />
              );
            }
            if (msg.sender === 'user' && msg.type === 'audio') {
              return <MockUserAudioBubble key={key} compact={compact} />;
            }
            return null;
          })}
        </div>
      </div>

      {/* Input area */}
      <div className='relative z-10 w-full pb-4'>
        <div className='flex justify-center px-4 pb-2'>
          <div className='w-full max-w-[174px]'>
            <SuggestedQuestions
              questions={[{ id: 'mock', text: suggestedQuestion }]}
              onQuestionSelect={() => {
                /* noop */
              }}
              disabled
            />
          </div>
        </div>
        <MockChatInput />
      </div>
    </div>
  );
};
